import type { QueryClient } from "@tanstack/react-query";
import { hasOrgPermission } from "@/app/capabilities/api/check-permission";
import {
  orgCapabilitiesQueryOptions,
  projectCapabilitiesQueryOptions,
} from "@/app/capabilities/api/get-capabilities";
import { ORG } from "@/app/capabilities/permissions";
import type {
  OrgCapabilities,
  ProjectCapabilities,
} from "@/app/capabilities/types";
import type { OrgProjectScope } from "@/lib/resource-context";
import { usageQueryOptions } from "./api/get-usage";

export function canReadUsage(
  projectCapabilities: ProjectCapabilities | undefined,
  orgCapabilities: OrgCapabilities | undefined
): boolean {
  if (projectCapabilities === undefined || orgCapabilities === undefined) {
    return false;
  }
  return (
    projectCapabilities.billing.enabled &&
    hasOrgPermission(orgCapabilities.authz, ORG.billing.read)
  );
}

export async function prefetchUsageIfReadable(
  queryClient: QueryClient,
  scope: OrgProjectScope
): Promise<void> {
  const capabilities = await Promise.all([
    queryClient.ensureQueryData(projectCapabilitiesQueryOptions(scope)),
    queryClient.ensureQueryData(
      orgCapabilitiesQueryOptions(scope.organizationId)
    ),
  ]).catch(() => undefined);

  if (capabilities === undefined) {
    return;
  }

  const [projectCapabilities, orgCapabilities] = capabilities;
  if (!canReadUsage(projectCapabilities, orgCapabilities)) {
    return;
  }

  queryClient.prefetchQuery(
    usageQueryOptions({ organizationId: scope.organizationId })
  );
}
